import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as currency
 * Safely handles NaN, null, or undefined values
 */
export function formatCurrency(amount: number | null | undefined): string {
  // Handle null, undefined, or NaN values - return $0 instead
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "$0";
  }
  
  // Use a cached formatter for better performance
  const formatter = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  
  return formatter.format(amount);
}

/**
 * Format a number as percentage
 */
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
}

/**
 * Round a number to a specified number of decimal places
 */
export function round(num: number, places: number = 2): number {
  const factor = Math.pow(10, places);
  return Math.round(num * factor) / factor;
}

/**
 * Calculate a value based on payment frequency
 * Safely handles NaN, null, or undefined values
 */
export function calculateByFrequency(
  amount: number | null | undefined,
  frequency: "weekly" | "fortnightly" | "monthly" | "annually"
): number {
  // Handle null, undefined, or NaN values
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 0;
  }
  
  switch (frequency) {
    case "weekly":
      return round(amount / 52);
    case "fortnightly":
      return round(amount / 26);
    case "monthly":
      return round(amount / 12);
    case "annually":
    default:
      return round(amount);
  }
}

/**
 * Format number with commas 
 */
export function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
